import { Lock } from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import { cn } from '@/lib/utils';
import { SegmentedControl } from './SegmentedControl';
import { SectionHeading } from './SectionHeading';
import type { SubscriptionModalFormState } from './useSubscriptionModalForm';

// Switch on/off (não há Switch no design system). Verde = cortesia ativa.
function Switch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
        checked ? 'bg-tertiary' : 'bg-border-strong'
      )}
    >
      <span
        className={cn(
          'inline-block size-5 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0.5'
        )}
      />
    </button>
  );
}

const inputClass =
  'w-full rounded-lg border border-border-strong bg-muted px-3 py-2 text-sm text-text-title placeholder:text-text-hint transition-colors focus:border-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-tertiary/40 disabled:cursor-not-allowed disabled:opacity-70';
const labelClass = 'mb-1.5 block text-xs font-medium text-text-secondary';
const readOnlyBox =
  'rounded-lg border border-border-strong bg-muted px-3 py-2 text-sm text-text-title';

interface FinanceiroSectionProps {
  form: SubscriptionModalFormState;
  disabled?: boolean;
}

// Chave com título e explicação (cortesia, cobrança automática).
function ChaveComTexto({
  titulo,
  descricao,
  checked,
  onChange,
  disabled,
}: {
  titulo: string;
  descricao: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border-strong bg-muted px-3 py-2.5">
      <div className="pr-3">
        <p className="text-sm font-medium text-text-title">{titulo}</p>
        <p className="text-xs text-text-secondary">{descricao}</p>
      </div>
      <Switch checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

// Chave de cortesia com título e explicação.
function CortesiaToggle(props: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  descricao: string;
}) {
  return <ChaveComTexto titulo="Cortesia" {...props} />;
}

// Seção "③ Financeiro" (modo Padrão: cortesia ou valor pago, editável) /
// "③ Financeiro do contrato" (modo Enterprise, editável, com cálculo de parcela).
export function FinanceiroSection({ form, disabled }: FinanceiroSectionProps) {
  const isEnterprise = form.planoCard === 'enterprise';

  if (isEnterprise) {
    const parcelaLabel =
      form.formaPagamento === 'mensal' && form.parcela != null
        ? `${formatCurrency(form.parcela)} × ${form.nMeses}`
        : form.formaPagamento === 'integral' && form.parcela != null
          ? formatCurrency(form.parcela)
          : '—';

    return (
      <div className="space-y-3">
        <SectionHeading
          num={3}
          tema="verde"
          title="Financeiro do contrato"
          trailing={
            <span className="inline-flex items-center rounded-full bg-tertiary/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-tertiary">
              Editável
            </span>
          }
        />
        <CortesiaToggle
          checked={form.enterpriseCortesia}
          onChange={form.setEnterpriseCortesia}
          disabled={disabled}
          descricao="Libera o contrato sem cobrança. Desligada, o valor entra nas Finanças."
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass} htmlFor="ent-valor-total">
              Valor total (R$)
            </label>
            <input
              id="ent-valor-total"
              type="number"
              min={0}
              step="0.01"
              disabled={disabled || form.enterpriseCortesia}
              value={form.valorTotal}
              onChange={(e) => form.setValorTotal(e.target.value)}
              className={inputClass}
              placeholder="0,00"
            />
          </div>
          <div>
            <label className={labelClass}>Forma</label>
            <SegmentedControl
              aria-label="Forma de pagamento"
              tema="verde"
              disabled={disabled || form.enterpriseCortesia}
              value={form.formaPagamento}
              onChange={form.setFormaPagamento}
              options={[
                { value: 'mensal', label: 'Mensal' },
                { value: 'integral', label: 'Integral' },
              ]}
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>Parcela</label>
          <div className={readOnlyBox}>{parcelaLabel}</div>
        </div>
        <div>
          <label className={labelClass} htmlFor="ent-notas">
            Notas (admin)
          </label>
          <textarea
            id="ent-notas"
            disabled={disabled}
            value={form.notasAdmin}
            onChange={(e) => form.setNotasAdmin(e.target.value)}
            className={inputClass}
            rows={2}
            placeholder="Ex: Contrato Enterprise #123 — Fazenda Três Irmãos"
          />
        </div>
      </div>
    );
  }

  const isTrial = form.planoCard === 'trial';
  // Trial é sempre grátis (sem toggle). Básico/Profissional podem ser cortesia
  // (grátis, status comp) ou assinatura paga (status active, com valor/período).
  const gratis = isTrial || form.cortesia;
  const anual = form.ciclo === 'anual';

  return (
    <div className="space-y-3">
      <SectionHeading
        num={3}
        tema="azul"
        title="Financeiro"
        trailing={
          gratis ? (
            <span className="inline-flex items-center gap-1 text-xs text-text-secondary">
              <Lock size={13} />
              cortesia
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
              Editável
            </span>
          )
        }
      />

      {!isTrial && (
        <CortesiaToggle
          checked={form.cortesia}
          onChange={form.setCortesia}
          disabled={disabled}
          descricao="Concede o plano gratuitamente, sem cobrança."
        />
      )}

      {gratis ? (
        <div>
          <label className={labelClass}>Valor</label>
          <div className={cn(readOnlyBox, 'font-medium text-tertiary')}>Grátis</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass} htmlFor="padrao-valor">
                {anual ? 'Valor pago no ano (R$)' : 'Valor do mês (R$)'}
              </label>
              <input
                id="padrao-valor"
                type="number"
                min={0}
                step="0.01"
                inputMode="decimal"
                disabled={disabled}
                value={form.valorPago}
                onChange={(e) => form.setValorPago(e.target.value)}
                className={inputClass}
                placeholder="0,00"
                aria-invalid={form.valorPagoNumero == null}
              />
            </div>
            <div>
              <label className={labelClass}>Forma</label>
              <div className={readOnlyBox}>{anual ? 'Integral, de uma vez' : 'Mensal'}</div>
            </div>
          </div>
          {form.valorTabela != null && form.valorPagoNumero !== form.valorTabela && (
            <p className="text-xs text-text-secondary">
              Tabela do plano: {formatCurrency(form.valorTabela)}
              {anual ? ' por ano' : ' por mês'}.
            </p>
          )}
          <ChaveComTexto
            titulo="Cobrança automática"
            descricao={
              form.cobrancaAutomatica
                ? `5 dias antes do fim do ${anual ? 'ano' : 'mês'}, o produtor recebe no WhatsApp o link para pagar por Pix ou boleto. Pagou, renova sozinho.`
                : `Manual: renovar pelo painel ao fim do ${anual ? 'ano' : 'mês'}.`
            }
            checked={form.cobrancaAutomatica}
            onChange={form.setCobrancaAutomatica}
            disabled={disabled}
          />
        </>
      )}
    </div>
  );
}
